import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, Flame } from "lucide-react";
import GifterLeaderboard from "@/components/GifterLeaderboard";
import TrendingSounds from "@/components/TrendingSounds";
import SuggestedCreators from "@/components/SuggestedCreators";
import GridTilePreview from "@/components/GridTilePreview";

export default function Discover() {
  const [trending, setTrending] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/discover/trending?period=week"),
      api.get("/videos/feed", { params: { limit: 24 } }),
    ]).then(([t, v]) => {
      setTrending(t.data.tags || []);
      setVideos(v.data);
    });
  }, []);

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-5xl mx-auto px-6 pt-10">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FFFF] mb-2">
          // global.signal
        </p>
        <h1 className="font-display font-black text-4xl mb-2 flex items-center gap-3">
          Discover
          <Flame className="h-7 w-7 text-[#FF007F]" />
        </h1>
        <p className="text-white/50 text-sm mb-10">
          What is burning hot across the grid right now.
        </p>

        {/* Personalized creator suggestions */}
        <div className="mb-12">
          <SuggestedCreators limit={12} />
        </div>

        {/* Top Gifters leaderboard */}
        <div className="mb-12">
          <GifterLeaderboard initialPeriod="week" compact={true} />
        </div>

        {/* Trending Sounds — horizontal carousel */}
        <TrendingSounds limit={12} />

        {trending.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-[#39FF14]" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
                trending tags
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {trending.map((t, idx) => (
                <Link
                  key={t.tag}
                  to={`/t/${t.tag}`}
                  data-testid={`discover-tag-${t.tag}`}
                  className="bg-[#0A0A0C] border border-white/10 p-4 hover:border-[#39FF14]/50 hover:bg-[#39FF14]/5 transition-all group"
                >
                  <p className="font-mono text-[10px] text-white/40 mb-1">
                    #{String(idx + 1).padStart(2, "0")}
                  </p>
                  <p className="font-display font-bold text-lg text-[#00FFFF] group-hover:text-[#39FF14] transition-colors break-all">
                    #{t.tag}
                  </p>
                  <p className="font-mono text-xs text-white/40 mt-1">
                    {t.count} posts
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
            latest drops
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {videos.map((v) => (
              <Link
                key={v.id}
                to={`/v/${v.id}?src=discover`}
                data-testid={`discover-video-${v.id}`}
                className="aspect-[9/16] bg-white/5 relative group overflow-hidden"
              >
                <GridTilePreview video={v} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-white text-xs">
                  <p className="truncate font-semibold">
                    @{v.author?.username}
                  </p>
                  <p className="font-mono text-[10px] text-white/60">
                    ♥ {v.like_count} · {(v.displayed_view_count ?? v.view_count) || 0} views
                  </p>
                </div>
              </Link>
            ))}
            {videos.length === 0 && (
              <div className="col-span-full text-center py-12 text-white/40">
                <p className="font-mono text-xs uppercase tracking-widest">
                  no signal yet
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
